import { z } from "zod";
import type { ErrorReason } from "./error-reasons.ts";

/**
 * The error codes the API returns. Each maps to one HTTP status, and the three
 * access layers of `07-entitlements-rbac.md` deliberately map to three
 * different ones: an unbought feature is a 402 with upsell metadata, a
 * forbidden action is a 403, and a breached quota is a 409 carrying the counts.
 * A merchant who did not buy coupons and a waiter who may not issue them must
 * not receive the same failure.
 */
export const ERROR_STATUS = {
  BAD_REQUEST: 400,
  VALIDATION_FAILED: 422,
  UNAUTHENTICATED: 401,
  FEATURE_NOT_ENTITLED: 402,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  QUOTA_EXCEEDED: 409,
  IDEMPOTENCY_CONFLICT: 409,
  RATE_LIMITED: 429,
  INTERNAL: 500,
} as const;

export type ErrorCode = keyof typeof ERROR_STATUS;

/**
 * One failed field of a `VALIDATION_FAILED` body, as it crosses the wire.
 *
 * Zod 4 defines `ZodError.issues` **non-enumerable**, so serialising the error
 * itself yields `{ name, message }` with every path buried inside `message` as
 * an escaped JSON string — on the wire, and unreadable to anything that did
 * not think to `JSON.parse` an error message. So the API flattens, and this is
 * the shape it flattens to.
 *
 * Defined here because both ends use it: the API's validation hook writes it,
 * and a form marking the field the server rejected reads it. Two spellings of
 * one path (`store.slug`) would mean a form that highlights nothing.
 */
export type ValidationIssue = { path: string; message: string };

export function toValidationIssues(error: {
  issues: readonly { path: PropertyKey[]; message: string }[];
}): ValidationIssue[] {
  return error.issues.map((issue) => ({
    path: z.core.toDotPath(issue.path),
    message: issue.message,
  }));
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: (typeof ERROR_STATUS)[ErrorCode];
  readonly details: unknown;
  /**
   * The translatable reason, where somebody has named one — see
   * `error-reasons.ts`. `undefined` on the great majority of throws, and that
   * is the state the client is built for: it falls back to `message`.
   */
  reason: ErrorReason | undefined;

  constructor(code: ErrorCode, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = ERROR_STATUS[code];
    this.details = details;
  }

  /**
   * Names the reason, and returns the error so a throw stays one expression:
   *
   * ```ts
   * throw new ApiError("CONFLICT", "Supplies carry this brand.").because("brandInUse");
   * ```
   *
   * A fourth constructor argument would have been the obvious move and is the
   * wrong one: `details` sits between, so the eighty throws that carry a reason
   * and no details would each have had to write `undefined` to reach past it,
   * and the reason — the part a reader is looking for — would land last.
   */
  because(reason: ErrorReason): this {
    this.reason = reason;
    return this;
  }

  toEnvelope() {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.reason === undefined ? {} : { reason: this.reason }),
        ...(this.details === undefined ? {} : { details: this.details }),
      },
    };
  }
}
